import { Prisma } from "@prisma/client";
import { BuyableItemEvent, BuyableItemHandler } from "../buyable-item.types";

type PartialExhibitorAdvancedStatisticsBuyableItem = {
  exhibitorAdvancedStatisticsEvent?: { id: string } | null;
  exhibitorAdvancedStatisticsStructure?: { id: string } | null;
};

type ExhibitorAdvancedStatisticsBuyableItem = {
  exhibitorAdvancedStatisticsEvent: { id: string };
  exhibitorAdvancedStatisticsStructure: { id: string };
};

type ExhibitorAdvancedStatisticsWithEvent = {
  exhibitorAdvancedStatisticsEvent: BuyableItemEvent;
};

const GROUP_KEY_PREFIX = "exhibitorAdvancedStatistics";

export const ExhibitorAdvancedStatisticsBuyableItemHandler = {
  displayName: "Statistiques avancées d'exposant",
  accounting: {
    generalAccountCode: "706504",
    invoiceLabel: "Statistiques avancées",
  },

  getEvent(item: ExhibitorAdvancedStatisticsWithEvent): BuyableItemEvent {
    return item.exhibitorAdvancedStatisticsEvent;
  },

  isValid(item: PartialExhibitorAdvancedStatisticsBuyableItem): boolean {
    return (
      !!item.exhibitorAdvancedStatisticsEvent?.id && !!item.exhibitorAdvancedStatisticsStructure?.id
    );
  },

  findEquivalent(
    item: PartialExhibitorAdvancedStatisticsBuyableItem,
  ): Prisma.BuyableItemWhereInput {
    return {
      exhibitorAdvancedStatisticsEvent: {
        id: item.exhibitorAdvancedStatisticsEvent?.id,
      },
      exhibitorAdvancedStatisticsStructure: {
        id: item.exhibitorAdvancedStatisticsStructure?.id,
      },
    };
  },

  create({
    exhibitorAdvancedStatisticsEvent,
    exhibitorAdvancedStatisticsStructure,
  }: ExhibitorAdvancedStatisticsBuyableItem): Omit<Prisma.BuyableItemCreateInput, "buyableType"> {
    return {
      exhibitorAdvancedStatisticsEvent: {
        connect: {
          id: exhibitorAdvancedStatisticsEvent.id,
        },
      },
      exhibitorAdvancedStatisticsStructure: {
        connect: {
          id: exhibitorAdvancedStatisticsStructure.id,
        },
      },
    };
  },

  parseGroupKeyAsWhere(groupKey: string): Prisma.BuyableItemWhereInput {
    if (!ExhibitorAdvancedStatisticsBuyableItemHandler.isGroupKey(groupKey)) {
      throw new Error(`Invalid group key for exhibitor advanced statistics: ${groupKey}`);
    }

    const [, eventId, structureId] = groupKey.split("|");
    return {
      exhibitorAdvancedStatisticsEvent: {
        id: eventId,
      },
      exhibitorAdvancedStatisticsStructure: {
        id: structureId,
      },
    };
  },

  isGroupKey(groupKey: string): boolean {
    return groupKey.startsWith(`${GROUP_KEY_PREFIX}|`);
  },

  getGroupKey(item: PartialExhibitorAdvancedStatisticsBuyableItem): string {
    const eventId = item.exhibitorAdvancedStatisticsEvent?.id;
    const structureId = item.exhibitorAdvancedStatisticsStructure?.id;
    if (!eventId || !structureId) {
      throw new Error("Cannot get group key for invalid exhibitorAdvancedStatistics item");
    }
    return `${GROUP_KEY_PREFIX}|${eventId}|${structureId}`;
  },

  getPricingQuery(): Prisma.PricingFindFirstArgs {
    return {
      where: {
        backofficePrestation: {
          type: "exhibitorAdvancedStatistics",
        },
      },
    };
  },

  getEventSearchFilter(searchText: string): Prisma.BuyableItemWhereInput {
    return {
      exhibitorAdvancedStatisticsEvent: {
        name: { contains: searchText, mode: "insensitive" },
      },
    };
  },
} as const satisfies BuyableItemHandler;
